import { Star } from 'lucide-react'
import { cn } from '../lib/cn'

/**
 * The review summary block: five bars on the left, the average and stars on
 * the right (frame 4763:9545).
 *
 * Bars are scaled against the most-voted score, not against the total. Against
 * the total, a provider with a healthy spread shows five short stubs and the
 * shape of their reputation is invisible — which is the one thing the block
 * exists to show.
 */
export function RatingSummary({
  average,
  count,
  histogram,
  className,
}: {
  average: number
  count: number
  /** Votes per score, index 0 = one star … index 4 = five stars. */
  histogram: number[]
  className?: string
}) {
  const peak = Math.max(1, ...histogram)

  return (
    <div
      className={cn(
        'flex items-center gap-6 rounded-lg border border-slate-200 px-2 py-3',
        className,
      )}
    >
      <ul className="flex flex-1 flex-col gap-2">
        {[5, 4, 3, 2, 1].map((score) => {
          const votes = histogram[score - 1] ?? 0
          return (
            <li key={score} className="flex items-center gap-2">
              <span className="flex w-5 shrink-0 items-center gap-0.5 text-xs text-slate-600">
                {score}
                <Star className="fill-warning text-warning size-2.5" aria-hidden="true" />
              </span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <span
                  className="bg-danger block h-full rounded-full"
                  style={{ width: `${(votes / peak) * 100}%` }}
                />
              </span>
              <span className="sr-only">
                {votes} avis à {score} étoile{score > 1 ? 's' : ''}
              </span>
            </li>
          )
        })}
      </ul>

      <div className="flex shrink-0 flex-col items-end gap-2">
        <p className="text-[28px] leading-[34px] font-bold text-slate-900">{average.toFixed(1)}</p>
        <Stars value={average} />
        <p className="text-xs text-slate-500">{count} avis</p>
      </div>
    </div>
  )
}

/**
 * Five stars with a half-filled one where the average lands.
 *
 * The half is drawn by clipping a filled star rather than by rounding: a 4.5
 * shown as five full stars is a quarter-point of flattery on every provider.
 */
export function Stars({ value, className }: { value: number; className?: string }) {
  return (
    <span
      className={cn('flex items-center gap-0.5', className)}
      role="img"
      aria-label={`${value.toFixed(1)} sur 5`}
    >
      {[0, 1, 2, 3, 4].map((i) => {
        const fill = Math.max(0, Math.min(1, value - i))
        return (
          <span key={i} className="relative inline-block size-3.5">
            <Star className="absolute inset-0 size-3.5 text-slate-300" aria-hidden="true" />
            <span
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${fill * 100}%` }}
              aria-hidden="true"
            >
              <Star className="fill-warning text-warning size-3.5" />
            </span>
          </span>
        )
      })}
    </span>
  )
}
